import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';
import { outreachApi } from '../outreach/api/outreachApi';

// Both clients take the same session, set here and nowhere else: the unread
// badge asks the outreach service from every page, not only from its own.
const applySession = (token) => {
  api.setToken(token);
  outreachApi.setToken(token);
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Set while rendering, not in an effect: children's effects run before this
  // component's, and the first request a child makes must already carry it.
  const [token, setToken] = useState(() => {
    const saved = localStorage.getItem('token');
    applySession(saved);
    return saved;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const userData = await api.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.login(email, password);
    const newToken = response.access_token;
    localStorage.setItem('token', newToken);
    applySession(newToken);
    setToken(newToken);
    await loadUser();
    return response;
  };

  const signInWithToken = async (newToken) => {
    localStorage.setItem('token', newToken);
    applySession(newToken);
    setToken(newToken);
    await loadUser();
  };

  const logout = () => {
    localStorage.removeItem('token');
    applySession(null);
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    login,
    signInWithToken,
    logout,
    updateUser,
    refreshUser: loadUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
